/**
 * BenefitDeltaList — Visual "what changes" list used in:
 *   - SubscriptionManagePage (canceled_active state)
 *   - CancelSubscriptionPage confirmation step
 *
 * Items are sourced from the premium feature registry by featureKey,
 * so this stays in sync with the paywall benefits.
 *
 * `mode`:
 *   - 'losing' → red X icon, struck-through text (used when Pro is ending)
 *   - 'gaining' → green check icon (used as positive reactivation reminder)
 */

import React from 'react';
import { X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface BenefitDeltaItem {
  /** Stable feature key (matches premium_features.feature_key). */
  featureKey: string;
  /** Translated label to render. */
  label: string;
}

interface BenefitDeltaListProps {
  items: BenefitDeltaItem[];
  mode: 'losing' | 'gaining';
  title?: string;
  className?: string;
}

export const BenefitDeltaList: React.FC<BenefitDeltaListProps> = ({
  items,
  mode,
  title,
  className,
}) => {
  if (items.length === 0) return null;

  return (
    <div className={cn('space-y-2', className)}>
      {title && (
        <p className="text-xs font-bold tracking-widest uppercase text-white/50">
          {title}
        </p>
      )}
      <ul className="space-y-1.5">
        {items.map(({ featureKey, label }) => (
          <li
            key={featureKey}
            className="flex items-center gap-2 text-sm text-white/80"
          >
            {mode === 'losing' ? (
              <X size={14} className="flex-shrink-0 text-[#D72E16]" />
            ) : (
              <Check size={14} className="flex-shrink-0 text-emerald-400" />
            )}
            <span className={mode === 'losing' ? 'line-through opacity-70' : ''}>
              {label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
